import { Vector3 } from 'three'
import type { Building } from '../world/cityGen'

/**
 * What the pilot can hit. The physics probes a handful of spheres along the
 * body each step and asks the world how deep they are; anything that can answer
 * that is a world (DESIGN.md §4 — "player capsule vs city AABBs (custom)",
 * no physics engine).
 */
export interface ContactWorld {
  /**
   * Deepest penetration of a sphere at `point` with `radius`.
   * @returns penetration depth in metres, 0 if clear. Writes the contact
   * normal (pointing out of the surface, toward the sphere) into `outNormal`.
   */
  probe(point: Vector3, radius: number, outNormal: Vector3): number
}

const UP = new Vector3(0, 1, 0)

/** Phase 1's world: an infinite plane at y = 0. */
export class FlatGround implements ContactWorld {
  probe(point: Vector3, radius: number, outNormal: Vector3): number {
    const penetration = radius - point.y
    if (penetration <= 0) return 0
    outNormal.copy(UP)
    return penetration
  }
}

/**
 * Ground plus axis-aligned building boxes, bucketed into a uniform grid so a
 * probe only tests the handful of boxes near it. A few hundred buildings would
 * be fine brute-force at 90 Hz, but the grid keeps it flat if the city grows.
 */
export class CityCollision implements ContactWorld {
  private readonly cellSize: number
  private readonly cells = new Map<number, number[]>()
  /** Flat [minX, minY, minZ, maxX, maxY, maxZ] per building. */
  private readonly boxes: Float32Array

  constructor(buildings: Building[], cellSize = 64) {
    this.cellSize = cellSize
    this.boxes = new Float32Array(buildings.length * 6)

    buildings.forEach((b, i) => {
      const o = i * 6
      const hw = b.width / 2
      const hd = b.depth / 2
      this.boxes[o] = b.x - hw
      this.boxes[o + 1] = 0
      this.boxes[o + 2] = b.z - hd
      this.boxes[o + 3] = b.x + hw
      this.boxes[o + 4] = b.height
      this.boxes[o + 5] = b.z + hd

      // A building can straddle several cells; register it in all of them.
      const x0 = Math.floor((b.x - hw) / cellSize)
      const x1 = Math.floor((b.x + hw) / cellSize)
      const z0 = Math.floor((b.z - hd) / cellSize)
      const z1 = Math.floor((b.z + hd) / cellSize)
      for (let cx = x0; cx <= x1; cx++) {
        for (let cz = z0; cz <= z1; cz++) {
          const key = cellKey(cx, cz)
          const bucket = this.cells.get(key)
          if (bucket) bucket.push(i)
          else this.cells.set(key, [i])
        }
      }
    })
  }

  probe(point: Vector3, radius: number, outNormal: Vector3): number {
    let deepest = radius - point.y
    if (deepest > 0) outNormal.copy(UP)
    else deepest = 0

    const cx = Math.floor(point.x / this.cellSize)
    const cz = Math.floor(point.z / this.cellSize)

    // The sphere can overlap a neighbouring cell, so sweep the 3x3 around it.
    for (let ix = cx - 1; ix <= cx + 1; ix++) {
      for (let iz = cz - 1; iz <= cz + 1; iz++) {
        const bucket = this.cells.get(cellKey(ix, iz))
        if (!bucket) continue
        for (const index of bucket) {
          const penetration = this.probeBox(index, point, radius, _normal)
          if (penetration > deepest) {
            deepest = penetration
            outNormal.copy(_normal)
          }
        }
      }
    }
    return deepest
  }

  /** Sphere vs AABB via the closest point on the box. */
  private probeBox(index: number, point: Vector3, radius: number, outNormal: Vector3): number {
    const o = index * 6
    const minX = this.boxes[o]
    const minY = this.boxes[o + 1]
    const minZ = this.boxes[o + 2]
    const maxX = this.boxes[o + 3]
    const maxY = this.boxes[o + 4]
    const maxZ = this.boxes[o + 5]

    const cx = clamp(point.x, minX, maxX)
    const cy = clamp(point.y, minY, maxY)
    const cz = clamp(point.z, minZ, maxZ)

    const dx = point.x - cx
    const dy = point.y - cy
    const dz = point.z - cz
    const distanceSq = dx * dx + dy * dy + dz * dz

    if (distanceSq > radius * radius) return 0

    if (distanceSq > 1e-8) {
      const distance = Math.sqrt(distanceSq)
      outNormal.set(dx / distance, dy / distance, dz / distance)
      return radius - distance
    }

    // Centre is inside the box: push out along the shallowest face, which is
    // the only choice that does not teleport a clipped pilot across the tower.
    //
    // The **bottom** face is deliberately not a candidate. It is the nearest
    // exit for anything that clips in near street level, and taking it ejects
    // the pilot downward through the pavement into an unrecoverable void. A
    // building has no accessible underside, so sideways or up are the only
    // honest answers.
    const toMinX = point.x - minX
    const toMaxX = maxX - point.x
    const toMaxY = maxY - point.y
    const toMinZ = point.z - minZ
    const toMaxZ = maxZ - point.z

    let best = toMinX
    outNormal.set(-1, 0, 0)
    if (toMaxX < best) {
      best = toMaxX
      outNormal.set(1, 0, 0)
    }
    if (toMaxY < best) {
      best = toMaxY
      outNormal.set(0, 1, 0)
    }
    if (toMinZ < best) {
      best = toMinZ
      outNormal.set(0, 0, -1)
    }
    if (toMaxZ < best) {
      best = toMaxZ
      outNormal.set(0, 0, 1)
    }
    return best + radius
  }
}

function cellKey(x: number, z: number): number {
  // Pack two signed cell coords into one number; ±32k cells is far more than
  // any city we will build.
  return ((x + 32768) << 16) | (z + 32768)
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

const _normal = new Vector3()
